import { useEffect, useMemo, useRef, useState } from "react";
import { apiUrl, CarId, PlayState } from "./protocol";
import { useAppLanguage } from "./i18n";
import {
  isCurrentTelemetryRequest,
  isStage2TelemetryRecord,
  Stage2TelemetryRecord,
  Stage2TelemetryHistoryPoint,
  TelemetryRequestIdentity,
  telemetryRaceTimeBucket,
  telemetryRetryDelayMs,
  throttleRawPercent,
} from "./stage2-telemetry-data";
import "./stage2-telemetry-panel.css";

interface TelemetryState {
  status: "loading" | "ready" | "error";
  record: Stage2TelemetryRecord | null;
  error: string;
}

export function useStage2Telemetry(
  carId: CarId,
  raceTimeMs: number,
  seekGeneration: number,
  playState: PlayState,
  dataFetchEnabled = true,
  onCurrentRecord?: (record: Stage2TelemetryRecord | null) => void,
): TelemetryState {
  const { t } = useAppLanguage();
  const translate = useRef(t);
  translate.current = t;
  const raceTimeBucketMs = useMemo(() => telemetryRaceTimeBucket(raceTimeMs), [raceTimeMs]);
  const desiredRequest = useRef({ carId, seekGeneration, raceTimeBucketMs });
  desiredRequest.current = { carId, seekGeneration, raceTimeBucketMs };
  const activeRequest = useRef<TelemetryRequestIdentity | null>(null);
  const nextRevision = useRef(0);
  const onCurrentRecordRef = useRef(onCurrentRecord);
  const lastAcceptedRecord = useRef<Stage2TelemetryRecord | null>(null);
  const [state, setState] = useState<TelemetryState>({ status: "loading", record: null, error: "" });
  // Playing requests follow the mutable desired bucket from the polling loop.
  // While paused, only a real clock/bucket change restarts this effect.
  const pausedRequestBucket = playState === "paused" ? raceTimeBucketMs : null;

  useEffect(() => { onCurrentRecordRef.current = onCurrentRecord; }, [onCurrentRecord]);

  useEffect(() => {
    const controller = new AbortController();
    if (!dataFetchEnabled) return () => controller.abort();
    let nextRequestTimer: ReturnType<typeof setTimeout> | null = null;
    let failedAttempts = 0;
    let disposed = false;
    const retained = lastAcceptedRecord.current?.car_id === carId
      && lastAcceptedRecord.current.seek_generation === seekGeneration
      ? lastAcceptedRecord.current : null;
    if (playState === "paused" && retained?.race_time_ms === raceTimeBucketMs) {
      setState({ status: "ready", record: retained, error: "" });
      onCurrentRecordRef.current?.(retained);
      return () => controller.abort();
    }
    setState({ status: "loading", record: retained, error: "" });
    if (!retained) onCurrentRecordRef.current?.(null);

    const scheduleRequest = (delayMs: number) => {
      if (disposed || controller.signal.aborted) return;
      if (nextRequestTimer !== null) clearTimeout(nextRequestTimer);
      nextRequestTimer = setTimeout(request, delayMs);
    };

    const retry = () => {
      if (disposed || controller.signal.aborted) return;
      failedAttempts += 1;
      setState(current => ({ status: "loading", record: current.record ?? retained, error: "" }));
      scheduleRequest(telemetryRetryDelayMs(failedAttempts));
    };

    const request = async () => {
      const desired = desiredRequest.current;
      if (desired.carId !== carId || desired.seekGeneration !== seekGeneration) return;
      const identity: TelemetryRequestIdentity = {
        ...desired, revision: ++nextRevision.current,
      };
      activeRequest.current = identity;
      try {
        const response = await fetch(apiUrl("/api/race/telemetry", {
          car_id: carId,
          race_time_ms: identity.raceTimeBucketMs,
          seek_generation: seekGeneration,
        }), { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error(`${translate.current("error.telemetryApi")}: ${response.status}`);
        const value = await response.json() as unknown;

        // 車両切替やシーク後に届いた古い応答は、現在の表示状態へ作用させない。
        if (disposed || controller.signal.aborted
          || !isCurrentTelemetryRequest(activeRequest.current, identity)) return;
        if (!isStage2TelemetryRecord(value, identity)) {
          retry();
          return;
        }

        failedAttempts = 0;
        lastAcceptedRecord.current = value;
        setState({ status: "ready", record: value, error: "" });
        onCurrentRecordRef.current?.(value);
        // Finish one local request before starting the next. The shared clock
        // may advance while this request is in flight; the next request then
        // immediately catches up to the newest bucket instead of aborting the
        // response every 100 ms and starving the graph of history.
        if (playState === "playing") {
          const latest = desiredRequest.current;
          scheduleRequest(latest.raceTimeBucketMs === identity.raceTimeBucketMs ? 100 : 0);
        }
      } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        if (disposed || controller.signal.aborted
          || !isCurrentTelemetryRequest(activeRequest.current, identity)) return;
        retry();
      }
    };

    void request();
    return () => {
      disposed = true;
      if (nextRequestTimer !== null) clearTimeout(nextRequestTimer);
      controller.abort();
    };
  }, [carId, dataFetchEnabled, pausedRequestBucket, playState, seekGeneration]);

  return state;
}

function Metric({ label, value, unit, accent = false }: {
  label: string;
  value: string;
  unit?: string;
  accent?: boolean;
}) {
  return <div className={accent ? "stage2-metric stage2-metric-accent" : "stage2-metric"}>
    <span>{label}</span><strong>{value}</strong>{unit && <small>{unit}</small>}
  </div>;
}

interface TelemetryVisualState {
  raceTimeMs: number;
  speedKmh: number;
  rpm: number;
  steering: number;
  throttle: number;
  brake: number;
  accelLongG: number | null;
  accelLatG: number | null;
  accelVertG: number | null;
  yawResponse: number;
  longitudinalResponse: number;
  turnRadS: number;
  driveG: number;
  distlapM: number;
  gear: number;
  lapCounter: number;
  tickIndex: number;
  history: Stage2TelemetryHistoryPoint[];
}

const TELEMETRY_GRAPH_DELAY_MS = 1_000;
const TELEMETRY_GRAPH_WINDOW_MS = 10_000;

const visualStateFromRecord = (record: Stage2TelemetryRecord): TelemetryVisualState => ({
  raceTimeMs: record.race_time_ms,
  speedKmh: record.speed_kmh,
  rpm: record.rpm,
  steering: record.steering,
  throttle: record.throttle,
  brake: record.brake,
  accelLongG: record.accel_long_g,
  accelLatG: record.accel_lat_g,
  accelVertG: record.accel_vert_g,
  yawResponse: record.yaw_response,
  longitudinalResponse: record.longitudinal_response,
  turnRadS: record.vehicle_response.turn_rad_s,
  driveG: record.vehicle_response.drive_g,
  distlapM: record.distlap_m,
  gear: record.gear,
  lapCounter: record.lap_counter,
  tickIndex: record.tick_index,
  history: record.history,
});

const mix = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress;

const mixNullable = (
  from: number | null, to: number | null, progress: number,
): number | null => {
  if (from === null || to === null) return progress < 1 ? from : to;
  return mix(from, to, progress);
};

const mixVisualState = (
  from: TelemetryVisualState, to: TelemetryVisualState, progress: number,
): TelemetryVisualState => ({
  raceTimeMs: Math.round(mix(from.raceTimeMs, to.raceTimeMs, progress)),
  speedKmh: mix(from.speedKmh, to.speedKmh, progress),
  rpm: mix(from.rpm, to.rpm, progress),
  steering: mix(from.steering, to.steering, progress),
  throttle: mix(from.throttle, to.throttle, progress),
  brake: mix(from.brake, to.brake, progress),
  accelLongG: mixNullable(from.accelLongG, to.accelLongG, progress),
  accelLatG: mixNullable(from.accelLatG, to.accelLatG, progress),
  accelVertG: mixNullable(from.accelVertG, to.accelVertG, progress),
  yawResponse: mix(from.yawResponse, to.yawResponse, progress),
  longitudinalResponse: mix(from.longitudinalResponse, to.longitudinalResponse, progress),
  turnRadS: mix(from.turnRadS, to.turnRadS, progress),
  driveG: mix(from.driveG, to.driveG, progress),
  distlapM: mix(from.distlapM, to.distlapM, progress),
  gear: to.gear,
  lapCounter: to.lapCounter,
  tickIndex: to.tickIndex,
  // Historical samples are immutable observations. Only their horizontal
  // position advances with visual time; interpolating their values would make
  // the recorded waveform deform in place.
  history: to.history,
});

function useSmoothTelemetryVisual(
  record: Stage2TelemetryRecord | null,
  carId: CarId,
  seekGeneration: number,
  playState: PlayState,
): TelemetryVisualState | null {
  const [visual, setVisual] = useState<TelemetryVisualState | null>(
    record ? visualStateFromRecord(record) : null,
  );
  const currentRef = useRef<TelemetryVisualState | null>(visual);
  const targetTimeRef = useRef<number | null>(record?.race_time_ms ?? null);
  const ownerRef = useRef(`${carId}:${seekGeneration}`);

  useEffect(() => {
    if (!record) return;
    const target = visualStateFromRecord(record);
    const owner = `${carId}:${seekGeneration}`;
    const previous = currentRef.current;
    const previousTargetTime = targetTimeRef.current;
    targetTimeRef.current = target.raceTimeMs;
    if (!previous || ownerRef.current !== owner || playState === "paused") {
      ownerRef.current = owner;
      currentRef.current = target;
      setVisual(target);
      return;
    }
    ownerRef.current = owner;
    const durationMs = Math.max(100, Math.min(300,
      target.raceTimeMs - (previousTargetTime ?? target.raceTimeMs - 100),
    ));
    const startedAt = performance.now();
    let frame = 0;
    const draw = (timestamp: number) => {
      const ratio = Math.max(0, Math.min(1, (timestamp - startedAt) / durationMs));
      const eased = ratio * ratio * (3 - 2 * ratio);
      const next = mixVisualState(previous, target, eased);
      currentRef.current = next;
      setVisual(next);
      if (ratio < 1) frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [carId, playState, record, seekGeneration]);

  return visual;
}

function useContinuousChartRaceTime(
  raceTimeMs: number,
  carId: CarId,
  seekGeneration: number,
  playState: PlayState,
): number {
  const delayedRaceTimeMs = Math.max(0, raceTimeMs - TELEMETRY_GRAPH_DELAY_MS);
  const [visualRaceTimeMs, setVisualRaceTimeMs] = useState(delayedRaceTimeMs);
  const visualRef = useRef(delayedRaceTimeMs);
  const ownerRef = useRef(`${carId}:${seekGeneration}`);

  useEffect(() => {
    const owner = `${carId}:${seekGeneration}`;
    if (ownerRef.current !== owner || playState === "paused") {
      ownerRef.current = owner;
      visualRef.current = delayedRaceTimeMs;
      setVisualRaceTimeMs(delayedRaceTimeMs);
      return;
    }

    // The shared media clock is published every 50 ms. Continue that clock at
    // playback rate between publications so the fixed samples travel left on
    // every animation frame instead of stopping at each telemetry response.
    const startedAt = performance.now();
    const startedRaceTimeMs = Math.max(visualRef.current, delayedRaceTimeMs);
    let frame = 0;
    const draw = (timestamp: number) => {
      const next = startedRaceTimeMs + timestamp - startedAt;
      visualRef.current = next;
      setVisualRaceTimeMs(next);
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [carId, delayedRaceTimeMs, playState, seekGeneration]);

  return visualRaceTimeMs;
}

const CHART_LEFT = 42;
const CHART_RIGHT = 596;
const CHART_TOP = 12;
const CHART_BOTTOM = 179;

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

function chartProgress(recordRaceTimeMs: number, visualRaceTimeMs: number): number {
  // During the first window, keep the recorded samples anchored to their
  // absolute position so the trace is acquired from left to right. At exactly
  // one window this is identical to the scrolling formula below, which avoids
  // a jump or a rescale at the handoff.
  if (visualRaceTimeMs < TELEMETRY_GRAPH_WINDOW_MS)
    return clamp01(recordRaceTimeMs / TELEMETRY_GRAPH_WINDOW_MS);
  return clamp01(1 - (visualRaceTimeMs - recordRaceTimeMs) / TELEMETRY_GRAPH_WINDOW_MS);
}

function chartHeadX(visualRaceTimeMs: number): number {
  const progress = visualRaceTimeMs < TELEMETRY_GRAPH_WINDOW_MS
    ? clamp01(visualRaceTimeMs / TELEMETRY_GRAPH_WINDOW_MS)
    : 1;
  return CHART_LEFT + progress * (CHART_RIGHT - CHART_LEFT);
}

function rightBoundaryValue(
  records: Stage2TelemetryHistoryPoint[],
  visualRaceTimeMs: number,
  value: (record: Stage2TelemetryHistoryPoint) => number | null | undefined,
): number | null {
  const afterIndex = records.findIndex(record => record.race_time_ms >= visualRaceTimeMs);
  if (afterIndex < 0) return null;
  const after = records[afterIndex];
  const afterValue = value(after);
  if (after.race_time_ms === visualRaceTimeMs)
    return afterValue !== null && afterValue !== undefined && Number.isFinite(afterValue) ? afterValue : null;
  if (afterIndex === 0) return null;
  const before = records[afterIndex - 1];
  const beforeValue = value(before);
  if (beforeValue === null || beforeValue === undefined || !Number.isFinite(beforeValue)
    || afterValue === null || afterValue === undefined || !Number.isFinite(afterValue)) return null;
  const ratio = (visualRaceTimeMs - before.race_time_ms)
    / (after.race_time_ms - before.race_time_ms);
  return mix(beforeValue, afterValue, ratio);
}

function chartPoints(
  records: Stage2TelemetryHistoryPoint[], visualRaceTimeMs: number,
  value: (record: Stage2TelemetryHistoryPoint) => number,
  normalize: (value: number) => number,
): string {
  if (!records.length) return "";
  const visible = records.filter(record => record.race_time_ms <= visualRaceTimeMs);
  const points = visible.map(record => {
    const progress = chartProgress(record.race_time_ms, visualRaceTimeMs);
    const x = CHART_LEFT + progress * (CHART_RIGHT - CHART_LEFT);
    const y = CHART_BOTTOM - clamp01(normalize(value(record))) * (CHART_BOTTOM - CHART_TOP);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  if (visible.length && points.length) {
    const first = visible[0];
    const firstProgress = chartProgress(first.race_time_ms, visualRaceTimeMs);
    if (firstProgress > 0) {
      const firstY = CHART_BOTTOM
        - clamp01(normalize(value(first))) * (CHART_BOTTOM - CHART_TOP);
      points.unshift(`${CHART_LEFT.toFixed(1)},${firstY.toFixed(1)}`);
    }
  }
  const boundaryValue = rightBoundaryValue(records, visualRaceTimeMs, value);
  if (boundaryValue !== null) {
    const y = CHART_BOTTOM - clamp01(normalize(boundaryValue)) * (CHART_BOTTOM - CHART_TOP);
    const boundary = `${chartHeadX(visualRaceTimeMs).toFixed(1)},${y.toFixed(1)}`;
    if (points.at(-1) !== boundary) points.push(boundary);
  }
  return points.join(" ");
}

function chartPath(
  records: Stage2TelemetryHistoryPoint[], visualRaceTimeMs: number,
  value: (record: Stage2TelemetryHistoryPoint) => number | null | undefined,
  normalize: (value: number) => number,
): string {
  if (!records.length) return "";
  let penDown = false;
  const visible = records.filter(record => record.race_time_ms <= visualRaceTimeMs);
  const commands = visible.flatMap(record => {
    const sample = value(record);
    if (sample === undefined || sample === null || !Number.isFinite(sample)) {
      penDown = false;
      return [];
    }
    const progress = chartProgress(record.race_time_ms, visualRaceTimeMs);
    const x = CHART_LEFT + progress * (CHART_RIGHT - CHART_LEFT);
    const y = CHART_BOTTOM - clamp01(normalize(sample)) * (CHART_BOTTOM - CHART_TOP);
    const command = penDown ? "L" : "M";
    penDown = true;
    return [`${command}${x.toFixed(1)},${y.toFixed(1)}`];
  });
  const first = visible[0];
  const firstSample = first ? value(first) : null;
  if (first && firstSample !== undefined && firstSample !== null && Number.isFinite(firstSample)) {
    const firstProgress = chartProgress(first.race_time_ms, visualRaceTimeMs);
    const firstX = CHART_LEFT + firstProgress * (CHART_RIGHT - CHART_LEFT);
    const firstY = CHART_BOTTOM
      - clamp01(normalize(firstSample)) * (CHART_BOTTOM - CHART_TOP);
    const firstCommand = `M${firstX.toFixed(1)},${firstY.toFixed(1)}`;
    if (firstProgress > 0 && commands[0] === firstCommand) {
      commands.splice(0, 1,
        `M${CHART_LEFT.toFixed(1)},${firstY.toFixed(1)}`,
        `L${firstX.toFixed(1)},${firstY.toFixed(1)}`,
      );
    }
  }
  const boundaryValue = rightBoundaryValue(records, visualRaceTimeMs, value);
  if (boundaryValue !== null) {
    const y = CHART_BOTTOM - clamp01(normalize(boundaryValue)) * (CHART_BOTTOM - CHART_TOP);
    commands.push(`${penDown ? "L" : "M"}${chartHeadX(visualRaceTimeMs).toFixed(1)},${y.toFixed(1)}`);
  }
  return commands.join(" ");
}

export function Stage2TelemetryPanel({
  carId, raceTimeMs, seekGeneration, playState, dataFetchEnabled = true, onCurrentRecord,
}: {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
  playState: PlayState;
  dataFetchEnabled?: boolean;
  onCurrentRecord?: (record: Stage2TelemetryRecord | null) => void;
}) {
  const { t, formatNumber } = useAppLanguage();
  const number = (value: number | null | undefined, digits = 1): string =>
    value === undefined || value === null ? "–" : formatNumber(value, {
      minimumFractionDigits: digits, maximumFractionDigits: digits,
    });
  const { status, record, error } = useStage2Telemetry(
    carId, raceTimeMs, seekGeneration, playState, dataFetchEnabled, onCurrentRecord,
  );
  const visual = useSmoothTelemetryVisual(record, carId, seekGeneration, playState);
  const history = visual?.history ?? [];
  const chartRaceTimeMs = useContinuousChartRaceTime(
    raceTimeMs, carId, seekGeneration, playState,
  );
  const chartHasFixedMargin = Boolean(record && record.race_time_ms >= chartRaceTimeMs);
  // A delayed or failed request must never erase already received history.
  // If the one-second safety margin is temporarily exhausted, freeze the
  // rendered head at the newest confirmed record until polling catches up.
  const renderedChartRaceTimeMs = record
    ? Math.min(chartRaceTimeMs, record.race_time_ms)
    : chartRaceTimeMs;
  const chartIsAcquiring = renderedChartRaceTimeMs < TELEMETRY_GRAPH_WINDOW_MS;
  const acquisitionHeadX = chartHeadX(renderedChartRaceTimeMs);
  const throttlePercent = visual ? throttleRawPercent(visual.throttle) : undefined;
  const lines = useMemo(() => ({
    throttle: chartPoints(history, renderedChartRaceTimeMs, item => item.throttle, value => value / 120),
    brake: chartPoints(history, renderedChartRaceTimeMs, item => item.brake, value => value / 120),
    steering: chartPoints(history, renderedChartRaceTimeMs, item => item.steering, value => .5 + value / 720),
    longitudinal: chartPoints(history, renderedChartRaceTimeMs, item => item.longitudinal_response, value => .5 + value / 4),
    yaw: chartPoints(history, renderedChartRaceTimeMs, item => item.yaw_response, value => .5 + value / 4),
    drive: chartPoints(history, renderedChartRaceTimeMs, item => item.drive_g, value => .5 + value / 4),
    accelLong: chartPath(history, renderedChartRaceTimeMs, item => item.accel_long_g, value => .5 + value / 8),
    accelLat: chartPath(history, renderedChartRaceTimeMs, item => item.accel_lat_g, value => .5 + value / 8),
    accelVert: chartPath(history, renderedChartRaceTimeMs, item => item.accel_vert_g, value => .5 + value / 8),
  }), [history, renderedChartRaceTimeMs]);
  return (
    <section
      className="stage2-telemetry-panel"
      data-testid="stage2-telemetry-panel"
      data-car-id={carId}
      data-seek-generation={seekGeneration}
      data-race-time-ms={record?.race_time_ms ?? telemetryRaceTimeBucket(raceTimeMs)}
      data-visual-race-time-ms={chartRaceTimeMs}
      data-rendered-race-time-ms={renderedChartRaceTimeMs}
      data-graph-delay-ms={TELEMETRY_GRAPH_DELAY_MS}
      data-graph-mode={chartIsAcquiring ? "acquiring" : "scrolling"}
      data-graph-cursor-x={acquisitionHeadX.toFixed(1)}
      data-graph-margin-ready={chartHasFixedMargin ? "true" : "false"}
      data-request-status={status}
      data-tick-index={record?.tick_index ?? ""}
      data-shadow-causal-id={record?.shadow_causal_id ?? ""}
    >
      <header><strong>DRIVING DATA</strong></header>
      <div className="stage2-chart-legend" aria-label={t("telemetry.legend")}>
        <span><i style={{ background: "#ff0000" }} />{t("telemetry.accelerator")}</span>
        <span><i style={{ background: "#0000ff" }} />{t("telemetry.brake")}</span>
        <span><i style={{ background: "#111111" }} />{t("telemetry.steering")}</span>
        <span><i style={{ background: "#b2b2b2" }} />{t("telemetry.response")}</span>
        <span><i style={{ background: "#ffd400" }} />{t("telemetry.longG")}</span>
        <span><i style={{ background: "#00e889" }} />{t("telemetry.latG")}</span>
        <span><i style={{ background: "#ff77d4" }} />{t("telemetry.vertG")}</span>
      </div>
      {error && <p className="stage2-telemetry-error" role="alert">{error}</p>}
      <div className="stage2-telemetry-primary">
        <Metric label="SPEED" value={number(visual?.speedKmh)} unit="km/h" accent />
        <Metric label="RPM" value={visual ? formatNumber(Math.round(visual.rpm)) : "–"} unit="rpm" accent />
        <Metric label="GEAR" value={visual ? String(visual.gear) : "–"} />
        <Metric label="LAP" value={visual ? String(visual.lapCounter) : "–"} />
      </div>
      <div className="stage2-telemetry-chart-wrap">
        <svg className="stage2-telemetry-chart" viewBox="0 0 610 210" role="img" aria-label={t("telemetry.chart")}>
          <defs>
            <pattern id="telemetry-acquisition-pending-pattern" width="8" height="8" patternUnits="userSpaceOnUse">
              <rect width="8" height="8" fill="rgba(0, 46, 72, .2)" />
              <path d="M-2 8L8-2M6 10L10 6" stroke="rgba(255, 255, 255, .13)" strokeWidth="1" />
            </pattern>
            <filter id="telemetry-acquisition-glow" x="-200%" y="-20%" width="500%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {[12, 53, 95, 137, 179].map(y => <line key={`h${y}`} className="stage2-chart-grid" x1="42" y1={y} x2="596" y2={y} />)}
          {[42, 153, 264, 375, 486, 596].map(x => <line key={`v${x}`} className="stage2-chart-grid" x1={x} y1="12" x2={x} y2="179" />)}
          {chartIsAcquiring && <rect
            data-testid="telemetry-acquisition-pending"
            className="stage2-chart-acquisition-pending"
            x={acquisitionHeadX}
            y={CHART_TOP}
            width={Math.max(0, CHART_RIGHT - acquisitionHeadX)}
            height={CHART_BOTTOM - CHART_TOP}
            fill="url(#telemetry-acquisition-pending-pattern)"
          />}
          <text className="stage2-chart-axis" x="13" y="16">1.0</text>
          <text className="stage2-chart-axis" x="13" y="99">0.5</text>
          <text className="stage2-chart-axis" x="13" y="183">0.0</text>
          {chartIsAcquiring ? <>
            <text className="stage2-chart-axis" x="36" y="198">0s</text>
            <text className="stage2-chart-axis" x="249" y="198">4s</text>
            <text className="stage2-chart-axis" x="471" y="198">8s</text>
            <text className="stage2-chart-axis" x="571" y="198">10s</text>
          </> : <>
            <text className="stage2-chart-axis" x="36" y="198">-11s</text>
            <text className="stage2-chart-axis" x="249" y="198">-7s</text>
            <text className="stage2-chart-axis" x="471" y="198">-3s</text>
            <text className="stage2-chart-axis" x="571" y="198">-1s</text>
          </>}
          <>
            <polyline className="stage2-chart-line" stroke="#b2b2b2" points={lines.longitudinal} />
            <polyline className="stage2-chart-line" stroke="#828282" points={lines.yaw} />
            <polyline className="stage2-chart-line" stroke="#d2d2d2" points={lines.drive} />
            <path className="stage2-chart-line" stroke="#ffd400" strokeWidth="2.1" d={lines.accelLong} />
            <path className="stage2-chart-line" stroke="#00e889" strokeWidth="2.1" d={lines.accelLat} />
            <path className="stage2-chart-line" stroke="#ff77d4" strokeWidth="1.8" d={lines.accelVert} />
            <polyline className="stage2-chart-line" stroke="#111111" strokeWidth="2.5" points={lines.steering} />
            <polyline className="stage2-chart-line" stroke="#0000ff" strokeWidth="2.3" points={lines.brake} />
            <polyline className="stage2-chart-line" stroke="#ff0000" strokeWidth="2.7" points={lines.throttle} />
          </>
          {chartIsAcquiring && <>
            <line
              data-testid="telemetry-acquisition-cursor"
              className="stage2-chart-acquisition-cursor"
              x1={acquisitionHeadX} y1={CHART_TOP} x2={acquisitionHeadX} y2={CHART_BOTTOM}
            />
            <circle
              className="stage2-chart-acquisition-head"
              cx={acquisitionHeadX} cy={CHART_TOP + 2.5} r="2.5"
            />
          </>}
          {!chartHasFixedMargin && record && <text className="stage2-chart-axis" x="255" y="105">DATA BUFFERING</text>}
        </svg>
        <div className="stage2-telemetry-secondary" aria-label={t("telemetry.current")}>
          <span>STEER <strong>{number(visual?.steering)}</strong>°</span>
          <span>THROTTLE <strong>{number(throttlePercent)}</strong>%</span>
          <span>BRAKE <strong>{number(visual?.brake)}</strong></span>
          <span>{t("telemetry.longG")} <strong>{number(visual?.accelLongG, 2)}</strong> G</span>
          <span>{t("telemetry.latG")} <strong>{number(visual?.accelLatG, 2)}</strong> G</span>
          <span>{t("telemetry.vertG")} <strong>{number(visual?.accelVertG, 2)}</strong> G</span>
          <span>YAW <strong>{number(visual?.yawResponse, 3)}</strong></span>
          <span>LONG <strong>{number(visual?.longitudinalResponse, 3)}</strong></span>
          <span>TURN <strong>{number(visual?.turnRadS, 3)}</strong></span>
          <span>DRIVE <strong>{number(visual?.driveG, 3)}</strong> G</span>
          <span>DIST <strong>{number(visual?.distlapM, 0)}</strong> m</span>
          <span className={`stage2-telemetry-status stage2-telemetry-status-${status}`}>
            {status === "ready" ? `TICK ${visual?.tickIndex ?? record?.tick_index}` : status === "error" ? "ERROR" : "SYNC"}
          </span>
        </div>
      </div>
    </section>
  );
}
